import VectorSource from 'ol/source/Vector'
import { Draw, Select } from 'ol/interaction'
import PointerInteraction from 'ol/interaction/Pointer'
import { shiftKeyOnly } from 'ol/events/condition'
import { ToolEnum } from '../model/tool.enum'
import { StyleUtils } from './style.utils'
import { Feature } from 'ol'
import { LineString, Point, Polygon } from 'ol/geom'
import { toLonLat, transform} from 'ol/proj'
import { getDistance, getLength } from 'ol/sphere'
import { Fill, RegularShape, Stroke, Style, Text } from 'ol/style'
import CircleStyle from 'ol/style/Circle'
import { FeatureLike } from 'ol/Feature'

export class InteractionUtils {
    static buildDefaultStyleFromColor(color: string): Style {
        return new Style({
        stroke: new Stroke({
            color: color,
            width: 2,
        }),
        fill: new Fill({
            color: `${color}80`,
        }),
        });
    }

    static buildAzimuthArrowStyle(feature: Feature): Style[] {
        const geometry = feature.getGeometry();
        const color = geometry?.getProperties()['color'] || '#ff0000';

        if (!(geometry instanceof LineString)) {
            console.warn('Invalid geometry for azimuth arrow, expected LineString');
            return [];
        }

        const coordinates = geometry.getCoordinates();
        if (coordinates.length < 2) {
            console.warn('Insufficient coordinates for azimuth arrow:', coordinates);
            return [];
        }

        const start = coordinates[0];
        const end = coordinates[coordinates.length - 1];
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];

        const rotation = Math.atan2(dy, dx);

        const arrowSize = 4;
        const arrowAngle = Math.PI / 4;

        const leftArm = [
            end[0] - arrowSize * Math.cos(rotation - arrowAngle),
            end[1] - arrowSize * Math.sin(rotation - arrowAngle),
        ];
        const rightArm = [
            end[0] - arrowSize * Math.cos(rotation + arrowAngle),
            end[1] - arrowSize * Math.sin(rotation + arrowAngle),
        ];

        const vShape = new LineString([leftArm, end, rightArm]);

        return [
            new Style({
            stroke: new Stroke({
                color: color,
                width: 3,
            }),
            }),
            new Style({
            geometry: vShape,
            stroke: new Stroke({
                color: '#000000',
                width: 7,
                lineCap: 'round',
            }),
            }),
        ];
    }

    static buildDeleteInteraction(...sources: VectorSource[]) {
        const deleteInteraction = new Select()
        deleteInteraction.on('select', event => {
            const selectedFeature = event.target.getFeatures().item(0)
            if (!selectedFeature) return
            for (const src of sources) {
                if (src.getFeatures().includes(selectedFeature)) {
                    src.removeFeature(selectedFeature)
                    break
                }
            }
            event.target.getFeatures().clear()
        })
        return deleteInteraction
    }

        static buildGenericDistanceInteraction(vectorSource: VectorSource): Draw {
        const distanceDraw = new Draw({
            source: vectorSource,
            maxPoints: null,
            type: 'LineString',
            style: (feature: FeatureLike): Style => {
            const geometry = feature.getGeometry();
            if (!(geometry instanceof LineString)) {
                return new Style();
            }
            return new Style({
                stroke: new Stroke({
                color: '#000000ff',
                width: 5,
                lineDash: [8, 11],
                }),
                text: new Text({
                text: `${Math.round(getLength(geometry) * 100) / 100} m`,
                font: 'bold 12px Arial',
                fill: new Fill({ color: '#000000' }),
                stroke: new Stroke({ color: '#ffffff', width: 3 }),
                offsetY: -10,
                placement: 'line',
                overflow: true,
                }),
            });
            },
        });
        distanceDraw.on('drawstart', event => {
            const geometry = event.feature.getGeometry();
            geometry.setProperties({ type: 'GenericDistance' });
        });
        return distanceDraw;
    }


    /**
     * Mode gomme pour les panneaux : shift+drag, et tous les panneaux que le curseur
     * survole pendant le drag sont supprimés en continu. Restreint aux panneaux —
     * surfaces et exclusions ne sont pas touchées.
     */
    static buildPanneauDragDeleteInteraction(panneauSource: VectorSource): PointerInteraction {
        const eraseUnderCursor = (evt: any) => {
            const features = evt.map.getFeaturesAtPixel(evt.pixel, { hitTolerance: 3 }) || []
            for (const f of features) {
                if (panneauSource.getFeatures().includes(f)) {
                    panneauSource.removeFeature(f)
                }
            }
        }
        return new PointerInteraction({
            handleDownEvent: (evt) => {
                if (!shiftKeyOnly(evt as any)) return false
                eraseUnderCursor(evt)
                return true // on prend la main → pas de pan map
            },
            handleDragEvent: (evt) => {
                eraseUnderCursor(evt)
            },
            handleUpEvent: () => false,
        })
    }

    static buildLengthInteraction(vectorSource: VectorSource, color: string): Draw {
        return this.buildTypedLengthInteraction(vectorSource, color, ToolEnum.Length)
    }

    static buildTypedLengthInteraction(
        vectorSource: VectorSource,
        color: string,
        toolType: ToolEnum,
        lineDash: number[] = [],
        width = 5
    ): Draw {
        const lengthDraw = new Draw({
            source: vectorSource,
            maxPoints: null,
            type: 'LineString',
            style: (feature) => {
                const geometryType = feature.getGeometry().getType()
                if (geometryType === 'LineString') {
                    return new Style({
                        stroke: new Stroke({
                            color,
                            width,
                            lineDash,
                            lineCap: 'round',
                        })
                    })
                }
                if (geometryType === 'Point') {
                    return new Style({
                        image: new CircleStyle({
                            radius: 4,
                            fill: new Fill({ color }),
                            stroke: new Stroke({ color, width: 1 }),
                        })
                    })
                }
                return null
            }
        })
        lengthDraw.on('drawstart', event => {
            event.feature.getGeometry().setProperties({ type: toolType, color })
        })
        return lengthDraw
    }

    static buildSquareInteraction(vectorSource: VectorSource, color: string, toolType: ToolEnum): Draw {
        const squareDraw = new Draw({
            source: undefined,
            type: 'Point',
            style: (feature) => {
                if (feature.getGeometry().getType() === 'Point') {
                    return new Style({
                        image: new CircleStyle({
                            radius: 4,
                            fill: new Fill({ color }),
                            stroke: new Stroke({ color, width: 1 }),
                        })
                    })
                }
                return null
            }
        })

        squareDraw.on('drawend', event => {
            const point = event.feature.getGeometry() as Point
            const [x, y] = point.getCoordinates()
            const halfSize = 3
            const polygon = new Polygon([[
                [x - halfSize, y - halfSize],
                [x + halfSize, y - halfSize],
                [x + halfSize, y + halfSize],
                [x - halfSize, y + halfSize],
                [x - halfSize, y - halfSize],
            ]])

            polygon.setProperties({ type: toolType, color })

            const polygonFeature = new Feature({
                geometry: polygon
            })

            polygonFeature.setStyle(new Style({
                stroke: new Stroke({
                    color,
                    width: 2,
                }),
                fill: new Fill({
                    color,
                }),
            }))

            vectorSource.addFeature(polygonFeature)
            vectorSource.removeFeature(event.feature)
        })

        return squareDraw
    }

    static buildAzimuthInteraction(vectorSource: VectorSource, color: string): Draw {
        const azimuthDraw = new Draw({
            source: vectorSource,
            maxPoints: 2,
            type: 'LineString',
            style: (feature) => [StyleUtils.buildDefaultStyleFromColor(color), ...StyleUtils.buildAzimuthArrowStyle(feature as Feature)]

        })
        azimuthDraw.on('drawstart', event => {
            event.feature.getGeometry().setProperties({ type: ToolEnum.Azimuth })
            const featuresToKeep = vectorSource.getFeatures().filter(feature => feature.getGeometry().getProperties()['type'] !== ToolEnum.Azimuth)
            vectorSource.clear()
            featuresToKeep.forEach(feature => vectorSource.addFeature(feature))
        })
        azimuthDraw.on('drawend', () => {
            vectorSource.once('addfeature', () => {});
        });
        return azimuthDraw
    }

    static buildExclusionInteraction(vectorSource: VectorSource): Draw {
        const exclusionStyle = new Style({
            fill: new Fill({ color: 'rgba(220, 30, 30, 0.30)' }),
            stroke: new Stroke({ color: '#dc1e1e', width: 2, lineDash: [6, 4] }),
            image: new CircleStyle({
                radius: 5,
                fill: new Fill({ color: '#dc1e1e' }),
                stroke: new Stroke({ color: '#dc1e1e', width: 2 }),
            }),
        })
        const draw = new Draw({
            source: vectorSource,
            type: 'Polygon',
            style: exclusionStyle,
        })
        draw.on('drawstart', event => {
            event.feature.getGeometry().setProperties({ type: ToolEnum.Exclusion })
        })
        draw.on('drawend', event => {
            const id = `exclusion-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
            event.feature.setId(id)
            event.feature.setProperties({ id, type: ToolEnum.Exclusion })
        })
        return draw
    }

    static buildAreaInteraction(vectorSource: VectorSource, color: string): Draw {
        const polygonStyle = new Style({
            fill: new Fill({ color: `${color}40` }),
            stroke: new Stroke({ color: color, width: 3 }),
        })
        const pointStyle = new Style({
            image: new CircleStyle({
                radius: 5,
                fill: new Fill({ color: color }),
                stroke: new Stroke({ color: color, width: 2 })
            })
        })

        const areaDraw = new Draw({
            source: vectorSource,
            maxPoints: null,
            type: 'Polygon',
            style: (feature) => {
                const geometryType = feature.getGeometry().getType()
                if (geometryType === 'Polygon') {
                    return polygonStyle
                }
                if (geometryType === 'Point') {
                    return pointStyle
                }
                return null
            }
        })
        areaDraw.on('drawstart', event => {
            event.feature.getGeometry().setProperties({ type: ToolEnum.Surface })
        })
        areaDraw.on('drawend', () => {
            vectorSource.once('addfeature', () => {});
        });
        return areaDraw
    }

    static buildBufferedLineAsAreaInteraction(vectorSource: VectorSource, color: string, width: number): Draw {
        const draw = new Draw({
          source: null,
          maxPoints: 2,
          type: 'LineString',
          style: (feature) => {
              const geom = feature.getGeometry()
              if (geom && geom.getType() === 'LineString') {
                  const line = geom as LineString
                  if (line.getCoordinates().length >= 2 && getLength(line) >= 0.01) {
                      const buffered = InteractionUtils.createBufferRectangleAroundLine(line, width)
                      const lineAngle = StyleUtils.computeLineAngle(line)
                      const panelStyle = StyleUtils.buildPanelStyleForAngle(color, lineAngle)
                      return new Style({
                          geometry: buffered,
                          fill: panelStyle.getFill(),
                          stroke: panelStyle.getStroke()
                      })
                  }
              }
              return StyleUtils.buildDefaultStyleFromColor(color)
          }
        })

        draw.on('drawend', (event) => {
            const line = event.feature.getGeometry() as LineString;
            if (!line || getLength(line) < 0.01) {
                return;
            }

            const bufferedPolygon = InteractionUtils.createBufferRectangleAroundLine(line, width);
            const polygonFeature = new Feature({
                geometry: bufferedPolygon
            });
            polygonFeature.getGeometry().setProperties({ type: ToolEnum.Surface });
            vectorSource.addFeature(polygonFeature);
            vectorSource.removeFeature(event.feature);
        });

        return draw
      }


    static createBufferRectangleAroundLine(line: LineString, width: number): Polygon {
        const coords = line.getCoordinates();
        const original = [];
        const right = [];

        for (let i = 0; i < coords.length - 1; i++) {
            const [x1, y1] = coords[i];
            const [x2, y2] = coords[i + 1];

            // Convertir en EPSG:4326 pour calcul géodésique
            const [lon1, lat1] = toLonLat([x1, y1], 'EPSG:3857');
            const [lon2, lat2] = toLonLat([x2, y2], 'EPSG:3857');

            const dx = x2 - x1;
            const dy = y2 - y1;
            const length = getDistance([lon1, lat1], [lon2, lat2]); // Longueur géodésique
            const offsetX = -(dy / length) * width;
            const offsetY = (dx / length) * width;

            original.push([x1, y1]);
            right.unshift([x1 + offsetX, y1 + offsetY]);

            if (i === coords.length - 2) {
            original.push([x2, y2]);
            right.unshift([x2 + offsetX, y2 + offsetY]);
            }
        }

        const ring = [...original, ...right, original[0]];
        return new Polygon([ring]);
    }
}
